import React, { useState, useEffect } from "react";
import "./CertificateSubmission.css";
import { Button, CircularProgress, TextField } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import GitHubIcon from "@mui/icons-material/GitHub";
import { useDispatch, useSelector } from "react-redux";
import { motion } from "framer-motion";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { submitUserCertificationRegistrationAction } from "../../../actions/MyCertificates/certificatesAction";

const CertificateSubmission = ({ enrollment }) => {
  const [showSubmission, setShowSubmission] = useState(false);
  const [githubUrl, setGithubUrl] = useState("");
  const [isValidUrl, setIsValidUrl] = useState(false);

  const dispatch = useDispatch();

  const { loading } = useSelector(
    (state) => state.certificationSubmitGithubLinkStore
  );

  useEffect(() => {
    if (githubUrl) {
      const match = githubUrl.match(
        /^https?:\/\/github\.com\/([^\/]+)\/([^\/]+)$/
      );
      setIsValidUrl(!!match);
    } else {
      setIsValidUrl(false);
    }
  }, [githubUrl]);

  const handleSubmit = () => {
    if (isValidUrl && !loading) {
      dispatch(
        submitUserCertificationRegistrationAction({
          userCertificationId: enrollment?._id,
          githubUrl: githubUrl,
        })
      );
    }
  };

  // Animation variants that respond to showSubmission state
  const containerVariants = {
    instructions: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
    submission: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  const contentVariants = {
    instructions: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    instructionsExit: {
      opacity: 0,
      x: -100,
      transition: { duration: 0.3, ease: "easeIn" },
    },
    submission: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4, ease: "easeOut", delay: 0.1 },
    },
    submissionInitial: {
      opacity: 0,
      x: 100,
    },
  };

  return (
    <motion.div
      className="certificateSubmission"
      variants={containerVariants}
      animate={showSubmission ? "submission" : "instructions"}
    >
      {showSubmission ? (
        <motion.div
          key="submission-view"
          variants={contentVariants}
          initial="submissionInitial"
          animate="submission"
          className="certificateSubmission_container"
        >
          <div className="certificateSubmission__header">
            <h1>Almost There! 🚀</h1>
            <p>Submit your GitHub repository to get certified</p>
          </div>

          <img
            src="images/certificate_submission_pageImage.png"
            alt="certificate submission"
            className="certificateSubmission__image"
          />

          <div className="certificateSubmission__keypoints">
            <div className="certificateSubmission__keypoint">
              <CheckCircleIcon className="certificateSubmission__keypoint-icon" />
              <span>Make sure your repo is public</span>
            </div>
            <div className="certificateSubmission__keypoint">
              <CheckCircleIcon className="certificateSubmission__keypoint-icon" />
              <span>Code is pushed and up to date</span>
            </div>
            <div className="certificateSubmission__keypoint">
              <CheckCircleIcon className="certificateSubmission__keypoint-icon" />
              <span>Include a README with your project details</span>
            </div>
          </div>

          <div className="certificateSubmission__input-wrapper">
            <TextField
              fullWidth
              value={githubUrl}
              onChange={(e) => setGithubUrl(e.target.value.trim())}
              placeholder="https://github.com/username/repository"
              variant="outlined"
              error={githubUrl && !isValidUrl}
              autoComplete="off"
              sx={{
                "& .MuiOutlinedInput-root": {
                  color: "#ffffff",
                  fontFamily: "Questrial, sans-serif",
                  fontSize: "14px",
                  "& fieldset": {
                    borderColor: "rgba(212, 175, 55, 0.6)",
                  },
                  "&:hover fieldset": {
                    borderColor: "rgba(255, 198, 9, 0.71)",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "rgba(255, 198, 9, 0.81)",
                  },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#666",
                  opacity: 1,
                },
              }}
              InputProps={{
                startAdornment: (
                  <GitHubIcon
                    sx={{
                      color: "#888",
                      fontSize: "26px",
                      marginRight: "12px",
                    }}
                  />
                ),
              }}
            />

            {githubUrl && !isValidUrl && (
              <p className="certificateSubmission__error">
                ⚠ Invalid URL format. Use: https://github.com/username/repo
              </p>
            )}

            {isValidUrl && (
              <div className="certificateSubmission__success">
                ✓ Valid repository URL detected
              </div>
            )}
          </div>

          <div className="certificateSubmission__button-wrapper">
            <Button
              className="certificateSubmission__submit-btn"
              disabled={!isValidUrl || loading}
              onClick={handleSubmit}
              fullWidth
            >
              {loading ? (
                <CircularProgress sx={{ color: "#1a0f04" }} size={22} />
              ) : (
                <>Submit Project</>
              )}
            </Button>
          </div>
        </motion.div>
      ) : (
        <motion.div
          key="instructions-view"
          variants={contentVariants}
          initial="instructions"
          animate={showSubmission ? "instructionsExit" : "instructions"}
          className="certificateSubmission_container"
        >
          <div className="certificateSubmission__intro">
            <h1>Project Submission</h1>
            <p>Follow the guide below to submit your project</p>
          </div>

          <div className="certificateSubmission__guide">
            <h2>How to Submit</h2>

            <div className="certificateSubmission__video-container">
              <iframe
                className="certificateSubmission__video"
                src="https://www.youtube.com/embed/dQw4w9WgXcQ"
                title="Submission Tutorial"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>

            <p className="certificateSubmission__divider">or</p>

            <p className="certificateSubmission__alt-text">
              Follow the step-by-step <a href="#pdf-link">PDF guide</a>
            </p>
          </div>

          <div className="certificateSubmission__action">
            <Button
              className="certificateSubmission__continue-btn"
              onClick={() => setShowSubmission(true)}
              fullWidth
            >
              Continue to Submission <ArrowForwardIcon />
            </Button>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default CertificateSubmission;
